/**
 * Authentication Context
 * Provides authentication state and methods throughout the app
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import authService from '../services/authService';
import i18n from '../i18n';
import {
  clearGuestLanguagePreference,
  getBrowserLanguagePreference,
} from '../utils/languagePreference';

const AuthContext = createContext(null);

/**
 * Hook to access authentication context
 * @returns {Object} Auth context value
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

/**
 * Authentication Provider Component
 * Wraps the app and provides authentication state and methods
 * 
 * SECURITY NOTE: 
 * - Access tokens are stored in memory only (not localStorage) - prevents XSS theft
 * - Refresh tokens are stored in httpOnly cookies (handled automatically by browser)
 * - On page refresh, access token is lost but can be refreshed using the cookie
 */
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authInitialized, setAuthInitialized] = useState(false);
  const [sessionConfirmed, setSessionConfirmed] = useState(false);
  const [error, setError] = useState(null);
  
  // Guard against React StrictMode double-execution
  const initializingRef = React.useRef(false);

  const applyAuthenticatedLanguage = useCallback((languageCode) => {
    if (languageCode) {
      void i18n.changeLanguage(languageCode);
    }
  }, []);

  const resetLoggedOutLanguage = useCallback(() => {
    clearGuestLanguagePreference();
    void i18n.changeLanguage(getBrowserLanguagePreference());
  }, []);

  // Initialize auth state
  // Note: Access token is in memory only, so after page refresh we need to re-authenticate
  // using the httpOnly refresh token cookie via session-restore endpoint
  useEffect(() => {
    const initAuth = async () => {
      // Prevent duplicate initialization (React StrictMode calls effects twice)
      if (initializingRef.current) {
        return;
      }
      initializingRef.current = true;
      
      try {
        // Try to get cached user first (user data is persisted in localStorage)
        const cachedUser = authService.getCachedUser();
        if (cachedUser) {
          setUser(cachedUser);
        }

        // If we have cached user info or a previously confirmed session in this tab,
        // attempt to restore auth state using the refresh cookie.
        const hadConfirmedSession = authService.isAuthenticated();
        const hasKnownSessionContext = cachedUser || hadConfirmedSession;
        if (hasKnownSessionContext) {
          // First, try to get current user (works if we have access token in memory)
          let currentUser = await authService.getCurrentUser();
          
          // If that failed (no in-memory access token after refresh/new tab), try session restore
          if (!currentUser) {
            currentUser = await authService.restoreSession();
          }
          
          if (currentUser) {
            setUser(currentUser);
            setSessionConfirmed(true);
            // Apply user's preferred language
            if (currentUser.preferredLanguage) {
              applyAuthenticatedLanguage(currentUser.preferredLanguage);
            }
          } else {
            // Token invalid or session restore failed, clear state
            setUser(null);
            setSessionConfirmed(false);

            // Keep re-auth modal global, but only show it for users who had
            // a previously confirmed authenticated session in this tab.
            if (hadConfirmedSession) {
              const email = cachedUser?.email || '';
              window.dispatchEvent(new CustomEvent('auth:reauth-needed', {
                detail: { email, reason: 'session_expired' }
              }));
            }
          }
        } else {
          setSessionConfirmed(false);
        }
      } catch (err) {
        console.error('Auth initialization failed:', err);
        setUser(null);
        setSessionConfirmed(false);
      } finally {
        setLoading(false);
        setAuthInitialized(true);
      }
    };

    initAuth();

    // Listen for logout events (from API interceptor)
    const handleLogout = () => {
      setUser(null);
      setSessionConfirmed(false);
      resetLoggedOutLanguage();
    };
    window.addEventListener('auth:logout', handleLogout);
    
    return () => {
      window.removeEventListener('auth:logout', handleLogout);
    };
  }, [applyAuthenticatedLanguage, resetLoggedOutLanguage]);

  /**
   * Register a new user
   */
  const register = useCallback(async (userData) => {
    setError(null);
    setLoading(true);
    try {
      const response = await authService.register(userData);
      if (response.success) {
        // Note: Registration no longer returns user/tokens - user must wait for approval
        // Don't set user here - they need to confirm email and get admin approval
        return { success: true, message: response.message };
      } else {
        setError(response.message);
        return { success: false, message: response.message };
      }
    } catch (err) {
      const message = err.message || 'Registration failed';
      setError(message);
      return { success: false, message };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Login with email and password
   * Note: If user has 2FA enabled, returns requiresTwoFactor: true
   * @param {string} email - User email
   * @param {string} password - User password
   * @param {boolean} [rememberMe=false] - Extend session to 30 days
   */
  const login = useCallback(async (email, password, rememberMe = false) => {
    setError(null);
    setLoading(true);
    try {
      const response = await authService.login(email, password, rememberMe);
      
      // If 2FA is required, don't set user yet - return the 2FA response
      if (response.requiresTwoFactor) {
        return {
          success: true,
          requiresTwoFactor: true,
          twoFactorUserId: response.twoFactorUserId,
          message: response.message
        };
      }

      // If password change is required, don't set user yet - return the password change response
      if (response.passwordChangeRequired) {
        return {
          success: true,
          passwordChangeRequired: true,
          userId: response.userId,
          email: response.email,
          message: response.message
        };
      }
      
      if (response.success) {
        setUser(response.user);
        setSessionConfirmed(true);
        // Apply user's preferred language on login
        if (response.user?.preferredLanguage) {
          applyAuthenticatedLanguage(response.user.preferredLanguage);
        }
        return { success: true };
      } else {
        setError(response.message);
        return { success: false, message: response.message };
      }
    } catch (err) {
      const message = err.message || 'Login failed';
      setError(message);
      return { success: false, message };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Logout current user
   */
  const logout = useCallback(async () => {
    setLoading(true);
    try {
      await authService.logout();
    } finally {
      setUser(null);
      setSessionConfirmed(false);
      setError(null);
      resetLoggedOutLanguage();
      setLoading(false);
    }
  }, [resetLoggedOutLanguage]);

  /**
   * Verify 2FA code during login (step 2 of 2FA login)
   * @param {string} userId - User ID from login response
   * @param {string} code - 6-digit code from authenticator app
   * @param {boolean} [rememberDevice=false] - Remember this device for 30 days (skip 2FA)
   * @param {boolean} [rememberMe=false] - Extend session to 30 days
   */
  const verify2FA = useCallback(async (userId, code, rememberDevice = false, rememberMe = false) => {
    setError(null);
    setLoading(true);
    try {
      const response = await authService.verify2FA(userId, code, rememberDevice, rememberMe);
      if (response.success) {
        setUser(response.user);
        setSessionConfirmed(true);
        // Apply user's preferred language after 2FA
        if (response.user?.preferredLanguage) {
          applyAuthenticatedLanguage(response.user.preferredLanguage);
        }
        return { success: true };
      } else {
        setError(response.message);
        return { success: false, message: response.message };
      }
    } catch (err) {
      const message = err.message || '2FA verification failed';
      setError(message);
      return { success: false, message };
    } finally {
      setLoading(false);
    }
  }, [applyAuthenticatedLanguage]);

  /**
   * Login using a recovery code (when authenticator app is unavailable)
   * @param {string} userId - User ID from login response
   * @param {string} recoveryCode - One of the 10 recovery codes
   * @param {boolean} [rememberMe=false] - Extend session to 30 days
   */
  const loginWithRecoveryCode = useCallback(async (userId, recoveryCode, rememberMe = false) => {
    setError(null);
    setLoading(true);
    try {
      const response = await authService.loginWithRecoveryCode(userId, recoveryCode, rememberMe);
      if (response.success) {
        setUser(response.user);
        setSessionConfirmed(true);
        // Apply user's preferred language after recovery code login
        if (response.user?.preferredLanguage) {
          applyAuthenticatedLanguage(response.user.preferredLanguage);
        }
        return { 
          success: true,
          recoveryCodesRemaining: response.recoveryCodesRemaining,
          warning: response.warning
        };
      } else {
        setError(response.message);
        return { success: false, message: response.message };
      }
    } catch (err) {
      const message = err.message || 'Recovery code login failed';
      setError(message);
      return { success: false, message };
    } finally {
      setLoading(false);
    }
  }, [applyAuthenticatedLanguage]);

  /**
   * Update user profile
   */
  const updateProfile = useCallback(async (updates) => {
    setError(null);
    try {
      const updatedUser = await authService.updateProfile(updates);
      setUser(updatedUser);
      setSessionConfirmed(!!updatedUser);
      return { success: true, user: updatedUser };
    } catch (err) {
      const message = err.message || 'Profile update failed';
      setError(message);
      return { success: false, message };
    }
  }, []);

  /**
   * Refresh user data from server
   */
  const refreshUser = useCallback(async () => {
    try {
      const currentUser = await authService.getCurrentUser();
      if (currentUser) {
        setUser(currentUser);
        setSessionConfirmed(true);
        return { success: true, user: currentUser };
      }
      setUser(null);
      setSessionConfirmed(false);
      return { success: false };
    } catch (err) {
      console.error('Failed to refresh user:', err);
      setSessionConfirmed(false);
      return { success: false, message: err.message };
    }
  }, []);

  /**
   * Clear any auth errors
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Check if current user has a specific role
   */
  const hasRole = useCallback((role) => {
    if (!sessionConfirmed) {
      return false;
    }

    return user?.roles?.includes(role) ?? false;
  }, [sessionConfirmed, user]);

  /**
   * Check if current user has any of the specified roles
   */
  const hasAnyRole = useCallback((roles) => {
    return roles.some(role => hasRole(role));
  }, [hasRole]);

  /**
   * Update user state directly (used by ReAuthModal after re-authentication)
   * @param {Object} newUser - The new user data
   */
  const updateUser = useCallback((newUser) => {
    setUser(newUser);
    setSessionConfirmed(!!newUser);
  }, []);

  const value = {
    // State
    user,
    loading,
    authInitialized,
    hasConfirmedSession: sessionConfirmed,
    error,
    isAuthenticated: sessionConfirmed && !!user,

    // Methods
    register,
    login,
    logout,
    verify2FA,
    loginWithRecoveryCode,
    updateProfile,
    refreshUser,
    clearError,
    hasRole,
    hasAnyRole,
    updateUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
